import { SortableTh, SortingDirection, Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { TagAccent } from '@components/core-ui/Tag/variations/TagAccent';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { useAuthContext } from '@providers/AuthProvider';
import { routes } from '@routes';
import { GetAllAttorneysTransactionsResponseTransactionDto } from 'app/API';
import { StyledLink } from '../../../MemberReportsPage/styles/StyledLink';
import { EntityIcon } from '@components/core-ui/EntityIcon/';
import { AvatarBlock } from './styles/AvatarBlock';
import { FlexCellContent } from './styles/FlexCellContent';
import { TypeCell } from './styles/TypeCell';

// it comes from the backend
// eslint-disable-next-line camelcase
const typesTextByValue = { attorney_connection: 'Connection', attorney_invitation: 'Invitation' };

type TProps = {
  attorneysTransactions: GetAllAttorneysTransactionsResponseTransactionDto[];
  sortingField: string;
  sortingReverse: boolean;
  toggleSort: (field: string) => void;
};

export const AttorneysTable = ({ attorneysTransactions, sortingField, sortingReverse, toggleSort }: TProps) => {
  const { organization } = useAuthContext();

  return (
    <TableWrapper>
      <Table>
        <Thead>
          <Tr>
            <SortableTh style={{ width: '1%' }} onClick={() => toggleSort('createdAt')}>
              Date
              {sortingField === 'createdAt' && <SortingDirection reverse={sortingReverse} />}
            </SortableTh>
            <Th>Email Address</Th>
            <Th>Job</Th>
            <Th>Inviter</Th>
            <Th>Status</Th>
            <Th>Type</Th>
            <Th>Stripe Invoice</Th>
            <Th>Price</Th>
          </Tr>
        </Thead>
        <Tbody>
          {attorneysTransactions.map((data, index) => {
            const key =
              data.type === 'attorney_connection' || data.type === 'attorney_invitation'
                ? data.type
                : 'attorney_connection';
            const typeText = typesTextByValue[key] ?? 'Unknown';

            return (
              <Tr key={data.id ?? index}>
                <Td>
                  <FlexCellContent>{getAmericanTime(data.createdAt)}</FlexCellContent>
                </Td>
                <Td>
                  <FlexCellContent>{data.attorneyEmail}</FlexCellContent>
                </Td>
                <Td>
                  <div className="flex flex-col">
                    <div className="flex flex-row items-center">
                      <TagAccent className="mr-2 text-[10px]">AS ONE</TagAccent>
                      <StyledLink
                        to={routes.jobDetails.make(
                          organization?.slug ?? '',
                          data.RecordingSession?.job?.Workspace?.slug || '',
                          data.RecordingSession?.jobId || '',
                        )}
                      >
                        {data.RecordingSession?.job?.name}
                      </StyledLink>
                    </div>
                    <span className="block text-[10px]/[160%] font-medium text-[var(--color-text-muted)]">
                      {data.RecordingSession?.jobId}
                    </span>
                  </div>
                </Td>
                <Td>
                  {!data.Inviter ? (
                    <div></div>
                  ) : (
                    <div className="flex gap-2.5 items-center">
                      <AvatarBlock className="flex-shrink-0">
                        <EntityIcon
                          className="flex-shrink-0"
                          name={`${data.Inviter.name} ${data.Inviter.lastname}`}
                          src={data.Inviter.avatar ?? undefined}
                        />
                      </AvatarBlock>
                      <div className="flex flex-col">
                        <span className="block">
                          {data.Inviter.name} {data.Inviter.lastname}
                        </span>
                        <span className="block text-[10px]/[160%] font-medium text-[var(--color-text-muted)]">
                          {data.Inviter.email}
                        </span>
                      </div>
                    </div>
                  )}
                </Td>
                <Td>
                  <FlexCellContent>
                    <p
                      style={{
                        textTransform: 'capitalize',
                        color: data.status === 'pending' ? 'var(--color-accent)' : 'var(--color-success-text)',
                        fontWeight: 500,
                      }}
                    >
                      {data.status}
                    </p>
                  </FlexCellContent>
                </Td>
                <Td>
                  <FlexCellContent>
                    <TypeCell color="var(--color-border)">{typeText}</TypeCell>
                  </FlexCellContent>
                </Td>
                <Td>
                  <FlexCellContent>{data.stripeChargeId || '-'}</FlexCellContent>
                </Td>
                <Td>
                  <FlexCellContent>{`$${data.priceAmount / 100}`}</FlexCellContent>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableWrapper>
  );
};
